import { useCallback, useState } from "react";

import { countryIcons } from "@similarweb/icons";
import { generateCollapsedText, PlainTooltip } from "@similarweb/ui-components";
import type { TextProps } from "@similarweb/ui-components/foundations";

import { StyledTruncatedText, StyledTruncatedTextIcon } from "./styles";

type Props = {
    height: number;
    text: string;
    textStyles: TextProps;
    country?: number;
};

const TruncatedTextWithTooltip = ({ height, text, textStyles, country }: Props) => {
    const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);
    const [truncatedText, setTruncatedText] = useState("");
    const [isLengthChecked, setIsLengthChecked] = useState(false);

    const refCallback = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            const isTextOverflowing = node.scrollHeight > node.clientHeight;
            setIsTooltipEnabled(isTextOverflowing);
            setIsLengthChecked(true);
            if (isTextOverflowing) {
                const reservedLength = country ? 23 : 3;
                const updatedText = generateCollapsedText(node, text);
                setTruncatedText(updatedText.substring(0, updatedText.length - reservedLength));
            }
        }
    }, []);

    if (country && country.toString() in countryIcons && isLengthChecked) {
        return (
            <PlainTooltip
                tooltipContent={text}
                placement="top"
                enabled={isTooltipEnabled}
                maxWidth={230}
            >
                <StyledTruncatedText
                    display="block"
                    $height={height}
                    {...textStyles}
                >
                    <StyledTruncatedTextIcon
                        dangerouslySetInnerHTML={{
                            __html: countryIcons[country.toString() as keyof typeof countryIcons],
                        }}
                    />
                    {isTooltipEnabled ? `${truncatedText}...` : text}
                </StyledTruncatedText>
            </PlainTooltip>
        );
    }

    return (
        <PlainTooltip
            tooltipContent={text}
            placement="top"
            enabled={isTooltipEnabled}
            maxWidth={230}
        >
            <StyledTruncatedText
                ref={refCallback}
                display="block"
                $height={height}
                {...textStyles}
            >
                {isTooltipEnabled ? `${truncatedText}...` : text}
            </StyledTruncatedText>
        </PlainTooltip>
    );
};

export default TruncatedTextWithTooltip;
